import { useState, useCallback, useEffect } from 'react';
import { getLoanById, getLoanSchedule } from '../../../services/loanService';
import type { Loan } from '../../LoansDashboard';

function getErrorMessage(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'message' in err && typeof (err as { message?: unknown }).message === 'string') {
    return (err as { message: string }).message;
  }
  return 'Network error';
}

export function useLoan(loanId: string | undefined) {
  const [loan, setLoan] = useState<Loan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLoan = useCallback(async () => {
    if (!loanId) {
      setError('Missing loan ID');
      setLoan(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [loanData, scheduleData] = await Promise.all([
        getLoanById(loanId),
        getLoanSchedule(loanId)
      ]);
      
      // Debug logs to see what data is being received
      
      // Backend returns different structures for different endpoints:
      // - /api/loans returns { records: [...] }
      // - /api/loans/:id returns the loan object directly
      // - /api/loans/:id/schedule returns the array directly
      const actualLoanData = loanData.records?.[0] || loanData;
      const actualScheduleData = Array.isArray(scheduleData) ? scheduleData : (scheduleData.records || []);
      
      
      // For single loan endpoint, loanData is already the loan object
      // For list endpoint, loanData.records[0] would be the first loan
      const finalLoanData = actualLoanData;
      
      
      const loanWithSchedule = { ...finalLoanData, schedule: Array.isArray(actualScheduleData) ? actualScheduleData : [] };
      
      
      setLoan(loanWithSchedule);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
      setLoan(null);
    }
    setLoading(false);
  }, [loanId]);

  // Only fetch when loanId changes, not on every render
  useEffect(() => {
    if (loanId) {
      fetchLoan();
    }
  }, [loanId, fetchLoan]);

  return { loan, loading, error, refetch: fetchLoan };
} 
